<template>
  <div id="blockList">
    <!-- Primary Actions + Selection Tools -->
    <div class="d-flex align-items-center mb-2">
      <b-button
        @click="selectFromMap()"
        variant="success"
        class="mr-2"
        v-if="$store.state.drawer.farmDetails.position === 'expand'"
      >
        Select on Map
      </b-button>
      <b-button
        @click="$store.commit('expandDrawer', 'farmDetails')"
        variant="success"
        class="mr-2"
        v-else
      >
        Full Screen Grid
      </b-button>
      <b-button
        @click="selectAllBlocks"
        variant="primary"
        class="mr-2"
        v-if="blocksData.length > 0"
      >
        Select All ({{ blocksData.length }})
      </b-button>
      <b-button
        @click="clearAllSelections"
        variant="secondary"
        class="mr-2"
        v-if="selectedBlocks.length > 0"
      >
        Clear All
      </b-button>
      <small class="text-muted" v-if="selectedBlocks.length > 0">
        {{ selectedBlocks.length }} selected
      </small>
    </div>
    <!-- Bulk Actions (only show when items are selected) -->
    <div class="mb-2" v-if="selectedBlocks.length > 0">
      <b-button @click="showBulkUpdateModal = true" variant="warning" class="mr-2">
        Bulk Update Selected Blocks
      </b-button>
      <b-dropdown variant="info" class="mr-2" text="Download Selected">
        <template #button-content>
          <i class="far fa-file-download"></i> Download Selected
        </template>
        <b-dropdown-item @click="downloadSelectedCSV()">CSV</b-dropdown-item>
        <b-dropdown-item @click="downloadSelectedGeoJSON()">GeoJSON</b-dropdown-item>
      </b-dropdown>
      <b-button @click="confirmDelete = true" variant="danger">
        Delete Selected Blocks
      </b-button>
    </div>
    <vue-good-table
      v-if="!hideTable"
      :rows="blocksData"
      :columns="tableColumns"
      :select-options="selectOptions"
      @on-selected-rows-change="onSelectionChange"
      @on-cell-click="handleCellClick"
      ref="blocksTable"
    />
    <BulkUpdateModal
      v-if="showBulkUpdateModal"
      :selectedBlocks="selectedBlocks"
      @closeModal="showBulkUpdateModal = false"
      @bulkUpdate="bulkUpdateBlocks"
    />
    <div
      class="overlay"
      style="padding-top:200px;"
      v-if="processingBlocks"
    >
      <p class="lead text-light text-center"><strong>Updating Blocks</strong></p>
      <b-progress
        class="ml-5 mr-5"
        :value="blockProcessing"
        :max="numberOfBlocks"
        show-progress
        animated
      ></b-progress>
    </div>
    <Confirm
      v-if="confirmDelete"
      text="WARNING: You are about to to delete all of these selected blocks. Are you sure you want to do that? This cannot be undone!!"
      v-on:confirmed="deleteSelectedBlocks($event)"
    />
  </div>
</template>

<script>
import { VueGoodTable } from 'vue-good-table'
import 'vue-good-table/dist/vue-good-table.css'
import { format } from 'date-fns'
import Confirm from '../Confirm'
import BulkUpdateModal from './BulkUpdateModal.vue'
import firebase from 'firebase/compat/app'
import Papa from 'papaparse'
const fb = require('../../firebaseConfig.js')
const turf = require('@turf/turf')

export default {
  data () {
    return {
      hideTable: false,
      confirmDelete: false,
      // selectedBlocks: [],
      showBulkUpdateModal: false,
      numberOfBlocks: 0,
      blockProcessing: 0,
      processingBlocks: false,
      selectedBlocksGeoJson: {
        type: 'FeatureCollection',
        features: []
      }
    }
  },
  components: {
    VueGoodTable,
    BulkUpdateModal,
    Confirm
  },
  created () {
    this.$store.commit('expandDrawer', 'farmDetails')
  },
  destroyed () {
    this.$store.commit('shrinkDrawer', 'farmDetails')
    this.clearAllSelections()
  },
  computed: {
    selectedBlocks: {
      get () {
        return this.$store.state.selectedBlocks
      },
      set (value) {
        this.$store.commit('setStateProperty', { property: 'selectedBlocks', value: value })
      }
    },
    blocksData () {
      return this.$store.state.blocksGeoJson.features
    },
    tableColumns () {
      const blockFields = this.$store.state.farm.blockFields
      const measurementSystem = this.$store.state.userProfile.measurementSystem
      const columns = [
        {
          label: 'Name',
          field: 'properties.name',
          tdClass: 'clickable anchor'
        },
        {
          label: 'Description',
          field: 'properties.description'
        },
        // Add the column for acres or hectares based on the user's measurement system preference
        ...(measurementSystem === 'Imperial' ? [{ label: 'Acres', field: 'properties.acres' }] : [{ label: 'Hectares', field: 'properties.hectares' }]),
        ...blockFields
          .filter(field => field.type !== 'Image') // filter out fields of type 'Image'
          .filter(field => !field.hidden) // filter out fields that are hidden
          .map(field => {
            const column = {
              label: field.label,
              field: `properties.${field.label}`
            }
            // If the field type is 'Date and Time', we add a formatFn to handle formatting
            if (field.type === 'Date and Time') {
              const dateTimeFormat = field.includeTime ? 'yyyy-MM-dd HH:mm' : 'yyyy-MM-dd'
              column.formatFn = (value) => value ? format(new Date(value * 1000), dateTimeFormat) : '-'
            }

            return column
          })
      ]
      return columns
    },
    selectOptions () {
      return {
        enabled: true,
        selectOnCheckboxOnly: true,
        selectionInfoClass: '',
        selectionText: 'rows selected',
        clearSelectionText: 'clear',
        disableSelectInfo: true,
        selectAllByGroup: true,
        rowKeyField: 'properties.blockId'
      }
    }
  },
  methods: {
    async highlightSelectedBlock (e) {
      const blockIdToToggle = e.features[0].properties.blockId

      // Toggle Check on the row
      this.blocksData.forEach((blockData, index) => {
        if (blockData.properties.blockId === blockIdToToggle) {
          console.log('Toggle Check')
          if (!this.blocksData[index].vgtSelected) {
            console.log('Selecting')
            this.$set(this.blocksData[index], 'vgtSelected', true)
            this.selectedBlocksGeoJson.features.push({
              type: 'Feature',
              properties: e.features[0].properties,
              geometry: e.features[0].geometry
            })
          } else {
            console.log('Deselecting')
            this.$set(this.blocksData[index], 'vgtSelected', false)
            // Filter out the deselected block
            this.selectedBlocksGeoJson.features = this.selectedBlocksGeoJson.features.filter(feature =>
              feature.properties.blockId !== blockIdToToggle
            )
          }
        }
      })

      // Remove the selected layer
      if (this.$store.state.mapObject.getLayer('selectBlockHighlightLayer')) {
        this.$store.state.mapObject.removeLayer('selectBlockHighlightLayer')
        this.$store.state.mapObject.removeSource('selectBlockHighlightLayer')
      }

      // Before adding the layer back, double-check if it's removed
      if (this.$store.state.mapObject.getLayer('selectBlockHighlightLayer')) {
        setTimeout(() => {
          this.addHighlightLayer()
        }, 100) // wait for 100ms before trying again
      } else {
        this.addHighlightLayer()
      }
    },
    // async highlightSelectedBlock (e) {
    //   const self = this
    //   // Select Checkbox on row
    //   this.blocksData.forEach((blockData, index) => {
    //     if (blockData.properties.blockId === e.features[0].properties.blockId) {
    //       console.log('Toggle Check')
    //       if (!this.blocksData[index].vgtSelected) {
    //         console.log('Selecting')
    //         this.$set(this.blocksData[index], 'vgtSelected', true)
    //         this.selectedBlocksGeoJson.features.push({
    //           type: 'Feature',
    //           properties: e.features[0].properties,
    //           geometry: e.features[0].geometry
    //         })
    //       } else {
    //         console.log('Deselecting')
    //         this.$set(this.blocksData[index], 'vgtSelected', false)
    //         // Deselect block
    //         for (let i = self.selectedBlocksGeoJson.features.length - 1; i >= 0; i--) {
    //           if (self.selectedBlocksGeoJson.features[i].properties.blockId === e.features[0].properties.blockId) {
    //             self.selectedBlocksGeoJson.features.splice(i, 1)
    //           }
    //         }
    //       }
    //     }
    //   })
    //   // REMOVE SELECTED LAYER
    //   if (self.$store.state.mapObject.getLayer('selectBlockHighlightLayer')) {
    //     self.$store.state.mapObject.removeLayer('selectBlockHighlightLayer')
    //     self.$store.state.mapObject.removeSource('selectBlockHighlightLayer')
    //   }

    //   console.log('Add selectBlockHighlightLayer')
    //   // Re-add selected blocks
    //   self.$store.state.mapObject.addLayer({
    //     id: 'selectBlockHighlightLayer',
    //     type: 'fill',
    //     source: {
    //       type: 'geojson',
    //       data: self.selectedBlocksGeoJson
    //     },
    //     layout: {},
    //     paint: {
    //       'fill-color': '#ffc107', // eslint-disable-line
    //       'fill-opacity': 0.8, // eslint-disable-line
    //     }
    //   })
    // },
    selectFromMap () {
      console.log('Select from Map')
      this.$store.commit('setStateProperty', { property: 'showKey', value: false })
      if (this.$store.state.mapObject.getLayer('blocksLayer')) {
        // this.$store.state.mapObject.removeLayer('blocksLayer')
        // this.$store.state.mapObject.removeLayer('blocksLayerOutline')
        // this.$store.state.mapObject.removeSource('blocksLayer')
        this.$store.state.mapObject.setLayoutProperty('blocksLayer', 'visibility', 'none')
        this.$store.state.mapObject.setLayoutProperty('blocksLayerOutline', 'visibility', 'none')
      }
      this.$store.commit('shrinkDrawer', 'farmDetails')
      this.$store.state.mapObject.addLayer({
        id: 'selectBlocksLayer',
        type: 'fill',
        source: {
          type: 'geojson',
          data: this.$store.state.blocksGeoJson
        },
        layout: {},
        paint: {
          'fill-color': '#6e59c7',
          'fill-opacity': 0.8
        }
      })
      this.$store.state.mapObject.addLayer({
        id: 'selectBlocksLayerOutline',
        type: 'line',
        source: {
          type: 'geojson',
          data: this.$store.state.blocksGeoJson
        },
        layout: {},
        paint: {
          'line-width': 2,
          'line-color': '#eee'
        }
      })
      const self = this
      this.$store.state.mapObject.on('mouseenter', 'selectBlocksLayer', function () {
        self.$store.state.mapObject.getCanvas().style.cursor = 'pointer'
      })
      this.$store.state.mapObject.on('mouseleave', 'selectBlocksLayer', function () {
        self.$store.state.mapObject.getCanvas().style.cursor = ''
      })
      // SELECT BLOCKS LAYER LISTENER CLICK
      this.$store.state.mapObject.on('click', 'selectBlocksLayer', this.highlightSelectedBlock)
      // REMOVE SELECTED LAYER
      if (this.$store.state.mapObject.getLayer('selectBlockHighlightLayer')) {
        this.$store.state.mapObject.removeLayer('selectBlockHighlightLayer')
        this.$store.state.mapObject.removeSource('selectBlockHighlightLayer')
      }

      // Before adding the layer back, double-check if it's removed to avoid potential timing issues
      // This is more of a brute-force method. Ideally, you might want to add some sort of delay or use Mapbox's events to verify layer removal
      if (this.$store.state.mapObject.getLayer('selectBlockHighlightLayer')) {
        setTimeout(() => {
          this.addHighlightLayer()
        }, 100) // wait for 100ms before trying again
      } else {
        this.addHighlightLayer()
      }
      // // Re-add selected blocks
      // console.log('Add selectBlockHighlightLayer')
      // this.$store.state.mapObject.addLayer({
      //   id: 'selectBlockHighlightLayer',
      //   type: 'fill',
      //   source: {
      //     type: 'geojson',
      //     data: this.selectedBlocksGeoJson
      //   },
      //   layout: {},
      //   paint: {
      //     'fill-color': '#ffc107', // eslint-disable-line
      //     'fill-opacity': 0.8, // eslint-disable-line
      //   }
      // })
    },
    handleCellClick (data) {
      if (data.column.label === 'Name') {
        fb.blocksCollection.doc(data.row.properties.blockId).get().then(doc => {
          const blockData = doc.data()
          blockData.id = doc.id
          this.$store.commit('setStateProperty', { property: 'block', value: blockData })
          this.$store.commit('hideDrawer', 'farmDetails')
          setTimeout(() => {
            this.$store.commit('showDrawer', 'blockDetails')
            this.$store.dispatch('highlightBlock', data.row)
          }, 300)
        }).catch(err => {
          console.log(err.message)
        })
      }
    },
    onSelectionChange ({ selectedRows }) {
      this.selectedBlocks = selectedRows
      console.log('Selection Change')
      console.log(this.selectedBlocks)

      // Add unmapped blocks
      selectedRows.forEach(selectedRow => {
        if (!this.selectedBlocksGeoJson.features.some(blockFeature => blockFeature.properties.blockId === selectedRow.properties.blockId)) {
          console.log('Row does not exist on map, add it')
          this.selectedBlocksGeoJson.features.push({
            type: 'Feature',
            properties: {
              blockId: selectedRow.properties.blockId
            },
            geometry: selectedRow.geometry
          })
        }
      })

      // Filter out unselected blocks
      this.selectedBlocksGeoJson.features = this.selectedBlocksGeoJson.features.filter(feature => {
        return selectedRows.some(row => row.properties.blockId === feature.properties.blockId)
      })

      this.updateHighlightLayerForBlocks()
    },
    addHighlightLayer () {
      if (!this.$store.state.mapObject) return
      if (this.$store.state.mapObject.getLayer('selectBlockHighlightLayer')) return
      this.$store.state.mapObject.addLayer({
        id: 'selectBlockHighlightLayer',
        type: 'fill',
        source: {
          type: 'geojson',
          data: this.selectedBlocksGeoJson
        },
        layout: {},
        paint: {
          'fill-color': '#ffc107',
          'fill-opacity': 0.8
        }
      })
    },
    selectAllBlocks () {
      this.selectedBlocks = this.blocksData.map(block => ({ ...block }))
      this.selectedBlocksGeoJson.features = this.blocksData.map(block => ({
        type: 'Feature',
        properties: { blockId: block.properties.blockId },
        geometry: block.geometry
      }))
      this.$nextTick(() => {
        if (this.$refs.blocksTable && this.blocksData) {
          this.blocksData.forEach((row, index) => {
            this.$set(this.blocksData[index], 'vgtSelected', true)
          })
        }
      })
      this.updateHighlightLayerForBlocks()
    },
    clearAllSelections () {
      this.selectedBlocks = []
      this.selectedBlocksGeoJson.features = []
      this.$nextTick(() => {
        if (this.$refs.blocksTable && this.blocksData) {
          this.blocksData.forEach((row, index) => {
            this.$set(this.blocksData[index], 'vgtSelected', false)
          })
        }
      })
      this.updateHighlightLayerForBlocks()
    },
    updateHighlightLayerForBlocks () {
      const mapObject = this.$store.state.mapObject
      if (!mapObject) return
      if (mapObject.getLayer('selectBlockHighlightLayer')) {
        mapObject.removeLayer('selectBlockHighlightLayer')
        mapObject.removeSource('selectBlockHighlightLayer')
      }
      if (mapObject.getLayer('selectBlockHighlightLayer')) {
        setTimeout(() => this.addHighlightLayer(), 100)
      } else {
        this.addHighlightLayer()
      }
    },
    downloadSelectedCSV () {
      if (!this.selectedBlocks.length) {
        this.$store.commit('setAlert', { active: true, message: 'No blocks selected for download.', level: 'alert-warning' })
        return
      }
      const itemHeaders = []
      const items = this.selectedBlocks.map(feature => {
        const props = feature.properties || {}
        const jsonObject = { ...props }
        if (jsonObject.createdOn != null) {
          jsonObject.createdOn = format(new Date(jsonObject.createdOn * 1000), 'yyyy-MM-dd HH:mm:ss')
        }
        if (jsonObject.updatedOn != null) {
          jsonObject.updatedOn = format(new Date(jsonObject.updatedOn * 1000), 'yyyy-MM-dd HH:mm:ss')
        }
        const blockFields = this.$store.state.farm.blockFields || []
        blockFields.forEach(field => {
          if (field.type === 'Date and Time' && jsonObject[field.label] != null) {
            const ts = jsonObject[field.label]._seconds != null ? jsonObject[field.label]._seconds : jsonObject[field.label]
            jsonObject[field.label] = field.includeTime
              ? format(new Date(ts * 1000), 'yyyy-MM-dd HH:mm')
              : format(new Date(ts * 1000), 'yyyy-MM-dd')
          }
        })
        try {
          const centroid = turf.centroid(feature)
          jsonObject.longitude = centroid.geometry.coordinates[0]
          jsonObject.latitude = centroid.geometry.coordinates[1]
        } catch (e) {
          jsonObject.longitude = null
          jsonObject.latitude = null
        }
        Object.keys(jsonObject).forEach(key => {
          if (!itemHeaders.includes(key)) itemHeaders.push(key)
        })
        return jsonObject
      })
      items.forEach(item => {
        itemHeaders.forEach(header => {
          if (!Object.prototype.hasOwnProperty.call(item, header)) {
            item[header] = null
          }
        })
      })
      const csv = Papa.unparse(items)
      const blob = new Blob([csv], { type: 'text/csv' })
      const urlObj = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = urlObj
      const farmName = (this.$store.state.farm && this.$store.state.farm.name) ? this.$store.state.farm.name.replace(/\s+/g, '_') : 'blocks'
      a.download = `${farmName}_selected_${this.selectedBlocks.length}_blocks.csv`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(urlObj)
      this.$store.commit('setAlert', {
        active: true,
        message: `Successfully downloaded ${this.selectedBlocks.length} selected blocks as CSV.`,
        level: 'alert-success',
        timeout: 3000
      })
    },
    downloadSelectedGeoJSON () {
      if (!this.selectedBlocks.length) {
        this.$store.commit('setAlert', { active: true, message: 'No blocks selected for download.', level: 'alert-warning' })
        return
      }
      const fc = {
        type: 'FeatureCollection',
        features: this.selectedBlocks.map(f => ({ type: f.type, properties: { ...f.properties }, geometry: f.geometry }))
      }
      const json = JSON.stringify(fc, null, 2)
      const blob = new Blob([json], { type: 'application/geo+json' })
      const urlObj = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = urlObj
      const farmName = (this.$store.state.farm && this.$store.state.farm.name) ? this.$store.state.farm.name.replace(/\s+/g, '_') : 'blocks'
      a.download = `${farmName}_selected_${this.selectedBlocks.length}_blocks.geojson`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(urlObj)
      this.$store.commit('setAlert', {
        active: true,
        message: `Successfully downloaded ${this.selectedBlocks.length} selected blocks as GeoJSON.`,
        level: 'alert-success',
        timeout: 3000
      })
    },
    // onSelectionChange ({ selectedRows }) {
    //   this.selectedBlocks = selectedRows
    //   console.log('Selection Change')
    //   console.log(this.selectedBlocks)
    //   // UPdate the map
    //   // Do all these selected Blocks Exist? Add unmapped block
    //   selectedRows.forEach(selectedRow => {
    //     let rowExistsOnMap = false
    //     this.selectedBlocksGeoJson.features.forEach(blockFeature => {
    //       if (blockFeature.properties.blockId === selectedRow.properties.blockId) {
    //         rowExistsOnMap = true
    //       }
    //     })
    //     if (!rowExistsOnMap) {
    //       console.log('Row does not exist on map, add it')
    //       const blockId = selectedRow.properties.blockId
    //       const eventData = {
    //         features: [
    //           {
    //             properties: {
    //               blockId: blockId
    //             },
    //             geometry: selectedRow.geometry
    //           }
    //         ]
    //       }
    //       // this.highlightSelectedBlock(eventData)
    //       this.selectedBlocksGeoJson.features.push({
    //         type: 'Feature',
    //         properties: eventData.features[0].properties,
    //         geometry: eventData.features[0].geometry
    //       })
    //     }
    //   })
    //   // Remove any mapped blocks that are not selected rows
    //   this.selectedBlocksGeoJson.features.forEach((selectedBlockGeoJson, me) => {
    //     let rowSelected = false
    //     selectedRows.forEach(selectedRow => {
    //       if (selectedRow.properties.blockId === selectedBlockGeoJson.properties.blockId) {
    //         rowSelected = true
    //       }
    //     })
    //     if (!rowSelected) {
    //       console.log('Deselect Block')
    //       console.log(selectedBlockGeoJson)
    //       console.log(me)
    //       // Deselect block
    //       for (let i = this.selectedBlocksGeoJson.features.length - 1; i >= 0; i--) {
    //         if (this.selectedBlocksGeoJson.features[i].properties.blockId === selectedBlockGeoJson.properties.blockId) {
    //           this.selectedBlocksGeoJson.features.splice(i, 1)
    //         }
    //       }
    //     } else {
    //       console.log('Keep Block')
    //     }
    //   })
    //   // REMOVE SELECTED LAYER
    //   if (this.$store.state.mapObject.getLayer('selectBlockHighlightLayer')) {
    //     this.$store.state.mapObject.removeLayer('selectBlockHighlightLayer')
    //     this.$store.state.mapObject.removeSource('selectBlockHighlightLayer')
    //   }
    //   // Re-add selected blocks
    //   console.log('Add selectBlockHighlightLayer')
    //   console.log(this.selectedBlocksGeoJson)
    //   this.$store.state.mapObject.addLayer({
    //     id: 'selectBlockHighlightLayer',
    //     type: 'fill',
    //     source: {
    //       type: 'geojson',
    //       data: this.selectedBlocksGeoJson
    //     },
    //     layout: {},
    //     paint: {
    //       'fill-color': '#ffc107', // eslint-disable-line
    //       'fill-opacity': 0.8, // eslint-disable-line
    //     }
    //   })
    // },
    async deleteSelectedBlocks (confirmed) {
      this.confirmDelete = false
      if (confirmed) {
        // If no blocks are selected, do nothing
        if (!this.selectedBlocks.length) return
        // Start progress
        this.processingBlocks = true
        this.numberOfBlocks = this.selectedBlocks.length
        this.blockProcessing = 0
        const db = fb.db
        for (const block of this.selectedBlocks) {
          try {
            // Delete block document from Firestore
            await db.collection('blocks').doc(block.properties.blockId).delete()
            // Increase processing
            this.blockProcessing++
          } catch (error) {
            console.error(`Error deleting block with ID: ${block.properties.blockId}`, error)
          }
        }
        await db.collection('farms').doc(this.$store.state.farm.id).update({
          recompileBlocks: true
        })
        // Refresh blocks list or map
        this.$store.dispatch('removeAllLayers')
        this.$store.commit('hideDrawer', 'farmDetails')
        setTimeout(() => {
          this.$store.dispatch('initFarmMap')
        }, 300)
      }
    },
    async bulkUpdateBlocks (property, newValue) {
      this.numberOfBlocks = this.selectedBlocks.length
      this.blockProcessing = 0
      this.processingBlocks = true
      // This method should implement the logic to update the specified property with the new value for all blocks in this.selectedBlocks
      const db = fb.db
      // Convert newValue to number if type is 'Number'
      if (property.type === 'Number') {
        newValue = Number(newValue)
      }

      // Convert newValue to Firestore timestamp if type is 'Date and Time'
      if (property.type === 'Date and Time') {
        if (!(newValue instanceof Date)) {
          console.log('Invalid Date Object', newValue)
          // Convert newValue string to Date object
          newValue = new Date(newValue)
        }
        newValue = firebase.firestore.Timestamp.fromDate(newValue)
      }

      const revisionMessage = `Updated ${property.label} as part of a bulk update.`

      for (const selectedBlock of this.selectedBlocks) {
        this.blockProcessing++
        // Get current block document from Firestore
        const blockDoc = await db.collection('blocks').doc(selectedBlock.properties.blockId).get()
        if (blockDoc.exists) {
          const blockData = blockDoc.data()
          
          // Create a clean copy for revision (without id field to avoid conflicts)
          const revisionData = { ...blockData }
          delete revisionData.id

          // Create a revision in the 'revisions' subcollection
          await db.collection('blocks').doc(selectedBlock.properties.blockId).collection('revisions').add(revisionData)

          // Update the block document with the new value
          const updateData = {}
          updateData[property.machine_name] = newValue
          updateData.revisionMessage = revisionMessage
          updateData.updatedOn = new Date()
          updateData.updatedBy = this.$store.state.currentUser.uid
          await db.collection('blocks').doc(selectedBlock.properties.blockId).update(updateData)
        }
      }
      await db.collection('farms').doc(this.$store.state.farm.id).update({
        recompileBlocks: true
      })
      this.$store.commit('hideDrawer', 'farmDetails')
      this.$store.dispatch('removeAllLayers')
      setTimeout(() => {
        this.$store.dispatch('initFarmMap')
      }, 300)
    }
  }
}
</script>

<style scoped>
::v-deep .anchor {
  text-decoration: underline;
}
::v-deep .anchor:hover {
  text-decoration: underline;
  font-weight: bold;
}
</style>
